import React, { lazy, Suspense, useEffect, useState } from 'react';

// Импортируем package.json для получения версий зависимостей
import packageJson from '../../../package.json';

import { ModuleFederationError } from '@shared/components/ModuleFederationError';
import { RemoteComponentWrapper } from '@shared/components/RemoteComponentWrapper';
import { RemoteModuleLoading } from '@shared/components/RemoteModule';

/**
 * Компонент для безопасной динамической загрузки удаленных Module Federation модулей
 */
export const DynamicRemoteComponent = ({
  remoteName,
  moduleExport = 'default',
  basename = '/',
  fallbackComponent = null,
  loadingComponent = null,
  ...props
}) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [RemoteComponent, setRemoteComponent] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadRemoteComponent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Динамически импортируем и инициализируем Module Federation
        const { init, loadRemote } = await import('@module-federation/enhanced/runtime');
        const { createRemoteAppComponent } = await import('@module-federation/bridge-react');

        // Получаем конфигурацию с query параметром для избежания кеширования
        const timestamp = Date.now();
        const remoteUrl = `${import.meta.env?.PUBLIC_MF_ASSETS_PREFIX}/module_federation/remoteEntry.js?t=${timestamp}`;

        // Инициализируем runtime только если еще не инициализирован
        if (!window.__FEDERATION_INITIALIZED__) {
          // Получаем версии из package.json
          const { dependencies } = packageJson;

          await init({
            name: `host_admin`,
            remotes: [
              {
                name: 'remote_d360v2',
                entry: remoteUrl,
              },
            ],
            shared: {
              react: {
                singleton: true,
                requiredVersion: dependencies.react,
                eager: true,
                strictVersion: false,
              },
              'react-dom': {
                singleton: true,
                requiredVersion: dependencies['react-dom'],
                eager: true,
                strictVersion: false,
              },
              antd: {
                singleton: true,
                requiredVersion: dependencies.antd,
                eager: false,
                strictVersion: false,
              },
              '@ant-design/cssinjs': {
                singleton: true,
                requiredVersion: dependencies['@ant-design/cssinjs'],
                eager: false,
                strictVersion: false,
              },
            },
          });

          window.__FEDERATION_INITIALIZED__ = true;
          console.log('✅ Module Federation runtime инициализирован');
        }

        // Создаем React-компонент из Module Federation приложения
        const AppComponent = createRemoteAppComponent({
          loader: async () => {
            console.log(`🔍 Загружаем ${remoteName}/${moduleExport}`);
            try {
              return await loadRemote(`${remoteName}/${moduleExport}`);
            } catch (error) {
              console.error(`❌ Модуль ${remoteName}/${moduleExport} не найден:`, error.message);
              throw new Error(`Модуль ${remoteName}/${moduleExport} не найден. Доступные модули: admin, user`);
            }
          },
          basename: basename,
          fallback: ({ error }) => {
            console.warn(`🚨 ${remoteName} module failed to load:`, error.message);
            return (
              <ModuleFederationError error={error} moduleName={remoteName} onRetry={() => window.location.reload()} />
            );
          },
          loading: <RemoteModuleLoading moduleName={remoteName} />,
        });

        if (isMounted) {
          setRemoteComponent(() => AppComponent);
          setIsLoading(false);
        }
      } catch (err) {
        console.error(`❌ Ошибка загрузки удаленного модуля ${remoteName}:`, err);

        if (isMounted) {
          setError(err);
          setIsLoading(false);
        }
      }
    };

    loadRemoteComponent();

    return () => {
      isMounted = false;
    };
  }, [remoteName, moduleExport, basename]);

  if (isLoading) {
    return loadingComponent || <RemoteModuleLoading moduleName={remoteName} />;
  }

  if (error) {
    if (fallbackComponent) {
      return fallbackComponent;
    }

    return <ModuleFederationError error={error} moduleName={remoteName} onRetry={() => window.location.reload()} />;
  }

  if (!RemoteComponent) {
    return fallbackComponent || <div>Удаленный компонент не загружен</div>;
  }

  return (
    <RemoteComponentWrapper>
      <Suspense fallback={<RemoteModuleLoading moduleName={remoteName} />}>
        <RemoteComponent {...props} />
      </Suspense>
    </RemoteComponentWrapper>
  );
};

export default DynamicRemoteComponent;
